import React, { useMemo } from 'react';
import { ReloadOutlined } from '@ant-design/icons';

import { Button, Divider, Flex, message, Table, Tooltip, type TableColumnsType } from 'antd';
import { useGetOrganizationsQuery, useGetPartnersQuery } from 'src/redux/services/centralApi';
import DashboardTemplate from 'src/templates/DashboardTemplate';
import CreateOrganization from 'src/pages/administration/OrganizationsPage/components/CreateOrganization';
import { Organization } from 'src/interfaces/entities/organization';
import DeleteOrganization from 'src/pages/administration/OrganizationsPage/components/DeleteOrganization';
import EditOrganization from 'src/pages/administration/OrganizationsPage/components/EditOrganization';
import CreateUserLink from 'src/pages/administration/OrganizationsPage/components/CreateUserLink';
import { User } from 'src/interfaces/entities/user';
import ListUsers from 'src/pages/administration/ListUsers';
import dayjs from 'dayjs';
import { FaIdBadge, FaKey } from 'react-icons/fa';

type OrgRow = Organization & { users: User[], jwt: string };

export default function OrganizationsPage() {
	const {
		data: organizationsData,
		isLoading: isLoadingOrganizations,
		isFetching: isFetchingOrganizations,
		refetch: refetchOrganizations,
	} = useGetOrganizationsQuery();
	const {
		data: partnerData,
		isLoading: isLoadingPartner,
		isFetching: isFetchingPartner,
		refetch: refetchPartner,
	} = useGetPartnersQuery({ size: 1000, });

	const columns = useMemo<TableColumnsType<OrgRow>>(
		() => [
			{
				dataIndex: 'name',
				title: 'Nome',
				key: 'name',
			},
			{
				dataIndex: 'partner_id',
				title: 'Parceiro',
				key: 'partner_id',
				render: (value: string) => {
					const partner = partnerData?.find((p) => p.id === value);
					return <span>{partner ? partner.name : 'N/A'}</span>;
				},
			},
			{
				dataIndex: 'created_at',
				title: 'Criado em',
				key: 'created_at',
				render: (value: string) => (
					<span>{dayjs(value).format('DD/MM/YYYY HH:mm')}</span>
				),
			},
			{
				title: 'Ações',
				key: 'actions',
				render: (_, record) => (
					<Flex gap={8} align='center'>
						<DeleteOrganization row={record} />
						<EditOrganization row={record} />
						<CreateUserLink orgId={record.id} />
						<Divider type='vertical' />
						<Tooltip title="Copiar ID">
							<Button
								type="text"
								onClick={() => {
									navigator.clipboard.writeText(record.id);
									message.success('ID copiado para a área de transferência');
								}}
								icon={<FaIdBadge />}
							/>
						</Tooltip>
						<Tooltip title="Copiar TOKEN">
							<Button
								type="text"
								onClick={() => {
									navigator.clipboard.writeText(record.jwt);
									message.success('TOKEN copiado para a área de transferência');
								}}
								icon={<FaKey />}
							/>
						</Tooltip>
					</Flex>
				),
			},
		],
		[ partnerData, ]
	);

	return (
		<DashboardTemplate pageTitle="Gerenciar Organizações">
			<Flex style={{ padding: 5, marginBottom: 8, }} gap={8} justify="end">
				<CreateOrganization />
				<Button
					type="text"
					onClick={() => {
						refetchOrganizations();
						refetchPartner();
					}}
					icon={<ReloadOutlined />}
				/>
			</Flex>
			<Table<OrgRow>
				columns={columns}
				dataSource={organizationsData ?? []}
				rowKey="id"
				loading={isLoadingOrganizations || isLoadingPartner || isFetchingOrganizations || isFetchingPartner}
				size="small"
				expandable={{
					expandedRowRender: (record) => <ListUsers users={record.users} />,
				}}
			/>
		</DashboardTemplate>
	);
}
